import { getImageBaseUrl, imageDefinition as imageDefinitions } from '../imageDefinition';
import type { Fabric, OptionsSelection, typeOptionValue } from '../interfaces';
import type { LayerDefinition } from '../preview/layers';
import type { ConfigurationTypes, ProductType } from '../productDefinition';
import { getModelFolderByProductType } from '../utils';
import { singleImage } from './transformation';

interface ImageDefinition {
  sizes: string[];
  srcSetWidths: number[];
  ratio: number;
}

const createSourcesets = (
  srcSetWidths: number[],
  sizeUrls: string[],
  ratio: number,
  noWidth: boolean,
  noTransformation?: boolean,
) => {
  return !noTransformation
    ? srcSetWidths
        .map(width => {
          const sizeUrl = sizeUrls.find(url =>
            noWidth ? url.includes(`${width * ratio}`) : url.includes(`${width}`) && url.includes(`${width * ratio}`),
          );
          return `${sizeUrl} ${width}w`;
        })
        .join(',')
    : '';
};

export const imageUrlBuilder = ({
  imageDefinition,
  imageUrls,
  noTransformation,
  cdnDomain,
  crop,
}: {
  imageDefinition: ImageDefinition;
  imageUrls: string[];
  noTransformation?: boolean;
  cdnDomain: string;
  crop?: string;
}) => {
  const { srcSetWidths, ratio, sizes } = imageDefinition;
  const imageBaseUrl = getImageBaseUrl(cdnDomain);

  const source = {
    src: '',
    srcSheet: '',
    srcRotation: '',
    srcset: '',
    srcsetRotation: '',
    sizes: sizes.join(','),
    ratio,
  };

  let transformations: string[] = [];
  let transformationSheet: string = '';
  const [fileName] = imageUrls;

  transformations = singleImage({ srcSetWidths, ratio, crop });
  transformationSheet = singleImage({ srcSetWidths: [imageDefinitions.sheetWidth], ratio, noWidth: true, crop })[0];

  const sizeUrls = transformations.map(
    transformation => `${imageBaseUrl}/${noTransformation ? 'f_auto/' : transformation + '/'}${fileName}`,
  );

  const lastIndex = fileName.lastIndexOf('/');
  const spriteFileName = fileName.slice(0, lastIndex) + '/rotation' + fileName.slice(lastIndex);
  const sizeUrlsSheet = `${imageBaseUrl}/${noTransformation ? 'f_auto/' : transformationSheet + '/'}${spriteFileName}_sheet`;

  const sizeUrlsRotation = transformations.map(
    transformation => `${imageBaseUrl}/${noTransformation ? 'f_auto/' : transformation + '/'}${fileName}_{rotation}`,
  );

  const index = Math.floor((srcSetWidths.length - 1) / 2);
  source.src = sizeUrls[index];
  source.srcSheet = sizeUrlsSheet;
  source.srcRotation = sizeUrlsRotation[index];
  source.srcset = createSourcesets(srcSetWidths, sizeUrls, ratio, false, noTransformation);
  source.srcsetRotation = createSourcesets(srcSetWidths, sizeUrlsRotation, ratio, true, noTransformation);

  return source;
};

/** Returns null when a placeholder has no value (e.g. an option hidden by business rules): no image exists for it. */
export const createLayerUrlFromTemplate = (
  stringWithTemplate: string,
  optionValues: typeOptionValue,
  optionsSelection: OptionsSelection,
  selectedFabric: Fabric,
  configurationTypeId: ConfigurationTypes,
): string | null => {
  let isResolved = true;
  const url = `${stringWithTemplate?.trim()}`
    .replace(/\{([^{}]*)\}/g, (_, key: string) => {
      const optionValue = optionValues[optionsSelection[configurationTypeId]?.[+key]];
      const selectedValue =
        key === 'fabricId' ? selectedFabric?.layerImageName : (optionValue?.layerImageName ?? optionValue?.imageName);

      if (selectedValue == null || selectedValue === '') {
        isResolved = false;
      }
      return `${selectedValue}`;
    })
    .trim();

  return isResolved ? url : null;
};

export const createModelViewUrl = (template: string, fabricCode: string, product: ProductType) => {
  const fabricSegment = `S${fabricCode.replace(/\//g, '-')}`;
  const productSegment = getModelFolderByProductType(product);

  return template
    .replace('{fabricId}', fabricSegment)
    .replace('{product}', productSegment).trim();
};

// Missing layer images are handled in the browser (previewFirstLayer swaps in the fallback,
// previewOtherLayers hides the layer), not checked here.
export const createPreviewData = (
  processedImageTemplates: (string | null)[],
  fallbackUrl: string,
  cdnDomain: string,
) => {
  const fallbackImage = imageUrlBuilder({
    imageUrls: [fallbackUrl],
    imageDefinition: imageDefinitions.layer,
    cdnDomain,
  });

  // An unresolved base layer cannot render: show the fallback alone, as the browser does when the base fails.
  if (processedImageTemplates[0] === null) {
    return { layers: [fallbackImage], fallbackImage };
  }

  const layers: LayerDefinition[] = processedImageTemplates.filter(tmp => tmp !== null).map(tmp =>
    imageUrlBuilder({
      imageUrls: [tmp],
      imageDefinition: imageDefinitions.layer,
      cdnDomain,
    }),
  );

  return {
    layers,
    fallbackImage,
  };
};

export const createConfigurationImageLayers = (processedImageTemplates: (string | null)[], cdnDomain: string) => {
  return processedImageTemplates.filter(tmp => tmp !== null).map(tmp => {
    return imageUrlBuilder({
      imageUrls: [tmp],
      imageDefinition: imageDefinitions.layer,
      cdnDomain,
    }).src;
  });
};
